import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import FullPageLoader from '@src/common/FullPageLoader';
import ReactSlider from '@src/common/ReactSliderRegister';
import { apikey, STATUS_CODES } from '@src/config/constant';
import { getMasterConfigID } from '@src/store/slices/common';
import { docs_upload, getAgencyBankList, getCityList, getFormFields, getStateList } from '@store/slices/commonId';
import { duplicateCheck,register as registerUser,sendOtpToRegistration} from '@src/store/slices/user';
import AddApplicationThankPopup from './insurance/AddApplicationThankPopup';
import RegisterDoc from './register/RegisterDoc';
import RegisterBasic from './register/RegisterBasic';
import RegisterPersonal from './register/RegisterPersonal';
import RegisterBank from './register/RegisterBank';
import RegisterOtpPopup from './register/RegisterOtpPopup';
import {
    buildRegisterOtpPayload,
    buildRegisterPayload,
    buildRegisterSectionDuplicateCheckPayloads,
    createUploadedRegisterUploadItem,
    flattenSectionFields,
    getRegisterUploadCategory,
    getRegisterUploadFile,
    getRegisterFormValues,
    getRegisterPrimaryContact,
    hasPendingRegisterUploads,
    prepareRegisterSections,
    REGISTER_DOCUMENT_UPLOAD_CATEGORY,
    syncRegisterUploadMetadata,
    validateRegisterSection,
} from './register/registerFormUtils';

const loginLeft = {
    backgroundImage: `url("/images/Group_1000005712.png")`,
    backgroundColor: "#0B1E51",
    backgroundRepeat: "repeat-y",
    backgroundPosition: "center",
    borderRadius: "25px"
};
const registerContentStyle = { width: "100%", maxWidth: "500px", margin: "0 auto" };

function Register() {
    const dispatch: any = useDispatch();
    const navigate = useNavigate();
    const commonState = useSelector((state: any) => state.common);
    const commonIdState = useSelector((state: any) => state.commonId);
    const registerApiHeaders = { apiKey: apikey };

    const [loading, setLoading] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [sectionList, setSectionList] = useState<any[]>([]);
    const [showRegisterOtpPopup, setShowRegisterOtpPopup] = useState(false);
    const [showRegisterSuccessPopup, setShowRegisterSuccessPopup] = useState(false);
    const [submittedRegisterValues, setSubmittedRegisterValues] = useState<Record<string, any>>({});
    const [otpSubmitting, setOtpSubmitting] = useState(false);

    const isSuccessfulResponse = (response: any) => response?.status === STATUS_CODES.success;

    const getMaxAccessibleStep = (sections: any[] = []) => {
        if (!sections.length) {
            return 0;
        }

        for (let sectionIndex = 0; sectionIndex < sections.length - 1; sectionIndex += 1) {
            const validationResult = validateRegisterSection(sections[sectionIndex]);

            if (!validationResult.isValid) {
                return sectionIndex;
            }
        }

        return sections.length - 1;
    };

    const maxAccessibleStep = getMaxAccessibleStep(sectionList);
    const getNextSectionList = (sectionIndex: number, nextSection: any) => {
        return sectionList.map((sectionItem: any, currentIndex: number) => {
            if (currentIndex !== sectionIndex) {
                return sectionItem;
            }

            return nextSection;
        });
    };

    const updateSection = (sectionIndex: number, updater: (sectionItem: any) => any) => {
        setSectionList((prevState: any[]) =>
            prevState.map((sectionItem: any, currentIndex: number) => {
                if (currentIndex !== sectionIndex) {
                    return sectionItem;
                }

                return updater(sectionItem);
            })
        );
    };

    const fetchRegisterFormConfig = async () => {
        setLoading(true);

        try {
            const result: any = await getFormFields({ formType: "register" }, registerApiHeaders);
            const formSections = result?.data?.insuranceFormFields || result?.data || result?.insuranceFormFields || [];

            if (!formSections?.length) {
                toast.error("Error in fetching register form config");
                return;
            }

            setSectionList(prepareRegisterSections(formSections));
        } catch (error) {
            toast.error("Unable to load register form config");
        } finally {
            setLoading(false);
        }
    };

    const uploadRegisterSectionFiles = async (sectionItem: any) => {
        const uploadCategory = getRegisterUploadCategory(sectionItem);

        if (!uploadCategory) {
            return sectionItem;
        }

        const nextSection = syncRegisterUploadMetadata(sectionItem);

        for (const fieldItem of flattenSectionFields(nextSection)) {
            if (!["file", "image"].includes(fieldItem?.type) || !(fieldItem?.uploaded_files || []).length) {
                continue;
            }

            const nextUploadedFiles = [];

            for (const uploadedItem of fieldItem.uploaded_files || []) {
                const rawFile = getRegisterUploadFile(uploadedItem);

                if (!rawFile) {
                    nextUploadedFiles.push({
                        ...uploadedItem,
                        upload_category: uploadedItem?.upload_category || uploadCategory,
                        ...(uploadCategory === REGISTER_DOCUMENT_UPLOAD_CATEGORY && uploadedItem?.document_id === undefined
                            ? { document_id: fieldItem?.id }
                            : {}),
                    });
                    continue;
                }

                const formData: any = new FormData();
                formData.append('upload_type', 'insurance_docs');
                formData.append('lead_id', 'register-user');
                formData.append('images', rawFile);

                const response: any = await docs_upload(formData);
                const uploadedFile = response?.data?.[0];

                if (!uploadedFile?.file_url) {
                    throw new Error("File upload failed");
                }

                nextUploadedFiles.push(
                    createUploadedRegisterUploadItem(uploadedItem, uploadedFile, {
                        upload_category: uploadCategory,
                        ...(uploadCategory === REGISTER_DOCUMENT_UPLOAD_CATEGORY ? { document_id: fieldItem?.id } : {}),
                    })
                );
            }

            fieldItem.uploaded_files = nextUploadedFiles;
            fieldItem.error_message = "";
        }

        return nextSection;
    };

    useEffect(() => {
        if (!commonState?.common || !Object.keys(commonState.common).length) {
            dispatch(getMasterConfigID(registerApiHeaders));
        }

        if ((commonIdState?.bankList || []).length === 0) {
            dispatch(getAgencyBankList(registerApiHeaders));
        }

        if ((commonIdState?.state || []).length === 0) {
            dispatch(getStateList(registerApiHeaders));
        }

        if ((commonIdState?.city || []).length === 0) {
            dispatch(getCityList({state: []}, registerApiHeaders));
        }

        fetchRegisterFormConfig();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!sectionList.length) {
            return;
        }

        const maxStepIndex = sectionList.length - 1;

        if (activeStep > maxStepIndex) {
            setActiveStep(maxStepIndex);
            return;
        }

        if (activeStep > maxAccessibleStep) {
            setActiveStep(maxAccessibleStep);
        }
    }, [activeStep, maxAccessibleStep, sectionList]);

    const handleSectionSubmit = async (sectionIndex: number) => {
        const currentSection = sectionList?.[sectionIndex];

        if (!currentSection) {
            return;
        }

        const validationResult = validateRegisterSection(currentSection);
        const validatedSection = validationResult.sectionItem;
        let nextSection = syncRegisterUploadMetadata(validatedSection);
        updateSection(sectionIndex, () => nextSection);

        if (!validationResult.isValid) {
            return;
        }

        const uploadCategory = getRegisterUploadCategory(nextSection);
        const hasPendingUploads = hasPendingRegisterUploads(nextSection);

        if (uploadCategory && hasPendingUploads) {
            setLoading(true);

            try {
                nextSection = await uploadRegisterSectionFiles(nextSection);
                updateSection(sectionIndex, () => nextSection);
            } catch (error) {
                toast.error("Unable to upload files right now.");
                return;
            } finally {
                setLoading(false);
            }
        }

        const nextSectionList = getNextSectionList(sectionIndex, nextSection);
        const registerValues = getRegisterFormValues(nextSectionList);
        const duplicateCheckPayloads = buildRegisterSectionDuplicateCheckPayloads(nextSection, registerValues);

        if (duplicateCheckPayloads.length > 0) {
            setLoading(true);

            try {
                for (const payloadItem of duplicateCheckPayloads) {
                    const duplicateCheckResponse = await duplicateCheck(payloadItem, false);

                    if (!isSuccessfulResponse(duplicateCheckResponse)) {
                        return;
                    }
                }
            } catch (error) {
                toast.error("Unable to validate duplicate details right now.");
                return;
            } finally {
                setLoading(false);
            }
        }

        if (sectionIndex < sectionList.length - 1) {
            setActiveStep(sectionIndex + 1);
            return;
        }

        setLoading(true);

        try {
            const otpPayload = buildRegisterOtpPayload(registerValues);

            if (!otpPayload?.email) {
                toast.error("Email is required to send OTP.");
                return;
            }

            const otpResponse = await sendOtpToRegistration({ email: otpPayload.email,source:"web" });

            if (!isSuccessfulResponse(otpResponse)) {
                return;
            }

            setSubmittedRegisterValues(registerValues);
            setShowRegisterOtpPopup(true);
        } catch (error) {
            toast.error("Unable to process registration right now.");
        } finally {
            setLoading(false);
        }
    };

    const handleRegisterOtpVerify = async (otp: string) => {
        const registerPayload = buildRegisterPayload(submittedRegisterValues, otp);
        const { email, name, mobile } = registerPayload;

        if (!email || !name || !mobile) {
            toast.error("Required registration details are missing.");
            return;
        }

        setOtpSubmitting(true);
        setLoading(true);

        try {
            const registerResponse = await registerUser({
                ...registerPayload,
                email,
                name,
                mobile,
                source:"web"
            });

            if (!isSuccessfulResponse(registerResponse)) {
                return;
            }

            setSubmittedRegisterValues((previousValues) => ({
                ...previousValues,
                otp,
            }));
            setShowRegisterOtpPopup(false);
            setShowRegisterSuccessPopup(true);
        } catch (error) {
            toast.error("Unable to complete registration right now.");
        } finally {
            setOtpSubmitting(false);
            setLoading(false);
        }
    };

    const handleRegisterOtpResend = async () => {
        const otpPayload = buildRegisterOtpPayload(submittedRegisterValues);

        if (!otpPayload?.email) {
            toast.error("Email is required to resend OTP.");
            return;
        }

        setLoading(true);

        try {
            await sendOtpToRegistration({ email: otpPayload.email });
        } catch (error) {
            toast.error("Unable to resend OTP right now.");
        } finally {
            setLoading(false);
        }
    };

    const navigateToLogin = () => {
        setShowRegisterSuccessPopup(false);
        navigate("/login");
    };

    const renderCurrentStep = () => {
        const currentSection = sectionList?.[activeStep];

        if (!currentSection) {
            return null;
        }

        const sectionProps = {
            sectionItem: currentSection,
            sectionIndex: activeStep,
            updateSection,
            onNext: () => handleSectionSubmit(activeStep),
            setLoading,
            requestHeaders: registerApiHeaders,
        };

        switch (currentSection?.key) {
            case "documents":
                return <RegisterDoc {...sectionProps} />;
            case "basic":
                return <RegisterBasic {...sectionProps} />;
            case "personal":
                return <RegisterPersonal {...sectionProps} />;
            case "bank":
                return <RegisterBank {...sectionProps} />;
            default:
                return <RegisterBasic {...sectionProps} />;
        }
    };

    return (
        <div className="container-fluid">
            <FullPageLoader show={loading} />
            <RegisterOtpPopup
                show={showRegisterOtpPopup}
                handleClose={() => setShowRegisterOtpPopup(false)}
                email={getRegisterPrimaryContact(submittedRegisterValues)?.email}
                onVerify={handleRegisterOtpVerify}
                onResend={handleRegisterOtpResend}
                verifying={otpSubmitting}
            />
            <AddApplicationThankPopup
                show={showRegisterSuccessPopup}
                handleClose={() => setShowRegisterSuccessPopup(false)}
                screenType="REGISTER"
                navigateToLogin={navigateToLogin}
            />
            <div className="row">
                <div className='register-main'>
                <div className="login-outer register-outer">
                    <div className="login-left-panel slider-left-pannel-web" style={loginLeft}>
                        <ReactSlider screenType="Register"></ReactSlider>
                    </div>

                    <div className="login-right-panel t-center">
                        <div className="insurance-create-outer w-100 register-tab-outer">
                            <div className='lead-detail-tabs'>
                                <div className="detail-form-sec">
                                    <div className="menu-tab-left-panel">
                                        <div className="dealer-tab-left-panel">
                                            <ul>
                                            {sectionList.map((sectionItem: any, sectionIndex: number) => {
                                                const isCurrentStep = sectionIndex === activeStep;
                                                const isStepDisabled = sectionIndex > maxAccessibleStep;
                                                const isCompletedStep = !isStepDisabled && validateRegisterSection(sectionItem).isValid;
                                                const sectionTabClassName = [
                                                    isCurrentStep ? "active" : "",
                                                    isCompletedStep ? "completed" : "",
                                                ].filter(Boolean).join(" ");

                                                return (
                                                   
                                                        <li  key={sectionItem?.key || sectionIndex}>
                                                        
                                                        <a
                                                            href="#!"
                                                            className={sectionTabClassName}
                                                            onClick={(event) => {
                                                                event.preventDefault();
                                                                if (isStepDisabled) {
                                                                    return;
                                                                }
                                                                setActiveStep(sectionIndex);
                                                            }}
                                                            aria-disabled={isStepDisabled}
                                                          
                                                        >
                                                            <span className='img-type'
                                                              
                                                            >
                                                                {sectionIndex + 1}
                                                            </span>
                                                            {sectionItem?.title}
                                                        </a>
                                                        </li>
                                                   
                                                );
                                            })}
                                             </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className='register-filed-outer'>
                                {renderCurrentStep()}
                            </div>
                        </div>
                    </div>
                </div>
                </div>
            </div>
        </div>
    );
}

export default Register
